<template>
  <div>
    <PageHeader v-if="!config.hideHeader" :title="config.title" :subtitle="config.subtitle">
      <slot name="header-actions" :reload="load" />
      <AppButton v-if="canCreate" icon="plus" @click="openCreate">{{ newLabel }}</AppButton>
    </PageHeader>
    <div v-else-if="canCreate" class="mb-4 flex justify-end">
      <AppButton icon="plus" @click="openCreate">{{ newLabel }}</AppButton>
    </div>

    <slot name="above" :list="list" :reload="load" />

    <div class="bento-card p-4 sm:p-5">
      <!-- Barre d'outils : recherche, filtres, période -->
      <div class="mb-4 flex flex-wrap items-end gap-3">
        <div v-if="config.search !== false" class="relative min-w-[14rem] flex-1">
          <AppIcon name="search" :size="16" class="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <label class="sr-only" for="list-search">Rechercher</label>
          <input id="list-search" v-model="list.search" type="search" class="input pl-10" :placeholder="config.searchPlaceholder || 'Rechercher…'" />
        </div>

        <div v-for="f in visibleFilters" :key="f.key" class="min-w-[10rem]">
          <label class="mb-1 block text-xs font-semibold text-slate-500 dark:text-slate-400" :for="`filter-${f.key}`">{{ f.label }}</label>
          <LookupSelect
            :id="`filter-${f.key}`"
            :model-value="filterValue(f)"
            :lookup="f.lookup || ''"
            :static-options="f.options || null"
            :blank="f.blank || 'Tous'"
            @update:model-value="setFilter(f, $event)"
          />
        </div>

        <template v-if="config.dateRange">
          <div>
            <label class="mb-1 block text-xs font-semibold text-slate-500 dark:text-slate-400" for="filter-from">Du</label>
            <input id="filter-from" v-model="list.extra.from" type="date" class="input" :max="list.extra.to || undefined" />
          </div>
          <div>
            <label class="mb-1 block text-xs font-semibold text-slate-500 dark:text-slate-400" for="filter-to">Au</label>
            <input id="filter-to" v-model="list.extra.to" type="date" class="input" :min="list.extra.from || undefined" />
          </div>
        </template>

        <AppButton v-if="hasActiveFilters" variant="ghost" size="sm" icon="x" @click="clearFilters">Effacer</AppButton>
        <slot name="toolbar-extra" :list="list" />
      </div>

      <AlertBanner v-if="rangeError" tone="warning" class="mb-4">{{ rangeError }}</AlertBanner>

      <ErrorState v-if="list.error && !list.items.length" :error="list.error" @retry="load" />
      <template v-else>
        <DataTable :columns="tableColumns" :rows="list.items" :loading="list.loading" :sort="list.sort" :clickable="!!config.detailRoute" @sort="toggleSort" @row-click="goDetail">
          <template v-for="col in config.columns" #[`cell-${col.key}`]="slotProps" :key="col.key">
            <slot :name="`cell-${col.key}`" v-bind="slotProps">
              <StatusBadge v-if="col.badge && slotProps.value !== null && slotProps.value !== undefined" :enum-name="col.badge" :value="String(slotProps.value)" />
              <span v-else :class="{ 'font-semibold text-slate-900 dark:text-slate-100': col.money }">{{ cellText(col, slotProps.row, slotProps.value) }}</span>
            </slot>
          </template>
          <template #cell-_actions="{ row }">
            <div class="flex items-center justify-end gap-1" @click.stop>
              <slot name="row-actions" :row="row" :reload="load" />
              <button v-if="canEdit(row)" type="button" class="btn-icon" :aria-label="`Modifier ${config.singular}`" title="Modifier" @click="openEdit(row)">
                <AppIcon name="edit" :size="16" />
              </button>
              <button v-if="canDelete(row)" type="button" class="btn-icon hover:!bg-rose-50 hover:!text-rose-600 dark:hover:!bg-rose-500/10 dark:hover:!text-rose-400" :aria-label="`Supprimer ${config.singular}`" title="Supprimer" @click="remove(row)">
                <AppIcon name="trash" :size="16" />
              </button>
            </div>
          </template>
          <template #empty>
            <EmptyState :icon="config.icon || 'file'" :title="hasActiveFilters ? 'Aucun résultat' : config.emptyTitle || 'Rien à afficher pour le moment'" :text="hasActiveFilters ? 'Modifiez ou effacez les filtres pour élargir la recherche.' : config.emptyText || ''">
              <AppButton v-if="canCreate && !hasActiveFilters" icon="plus" size="sm" @click="openCreate">{{ newLabel }}</AppButton>
            </EmptyState>
          </template>
        </DataTable>
        <AlertBanner v-if="list.error && list.items.length" class="mt-3">{{ list.error.message }}</AlertBanner>
        <AppPagination :meta="list.meta" @update:page="list.page = $event" @update:per-page="list.perPage = $event" />
      </template>
    </div>

    <ResourceForm v-if="config.fields" v-model="formOpen" :config="config" :record="editing" @saved="load" />
    <slot name="modals" :reload="load" />
  </div>
</template>

<script>
import PageHeader from '@/components/ui/PageHeader.vue'
import AppButton from '@/components/ui/AppButton.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import AppPagination from '@/components/ui/AppPagination.vue'
import AlertBanner from '@/components/ui/AlertBanner.vue'
import DataTable from '@/components/ui/DataTable.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ErrorState from '@/components/ui/ErrorState.vue'
import LookupSelect from '@/components/ui/LookupSelect.vue'
import StatusBadge from '@/components/ui/StatusBadge.vue'
import ResourceForm from './ResourceForm.vue'
import { onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { useList } from '@/composables/useList'
import { subscribeRealtime } from '@/stores/realtime'
import { session } from '@/stores/session'
import { toast } from '@/stores/toast'
import { confirmDialog } from '@/stores/confirm'
import { clearLookups } from '@/api/lookups'
import { daysBetween, formatDate } from '@/utils/dates'
import { demonstrative, newLabel } from '@/utils/labels'

/**
 * Page de liste générique pilotée par une configuration (config/*.js) :
 * colonnes, filtres, tri, pagination, formulaire, suppression avec gestion
 * des refus métier (409). Les boutons d'écriture n'apparaissent que si la
 * permission correspondante figure dans /auth/me.
 *
 * Les filtres et la période peuvent être préremplis par la query de la
 * route (?status=pending&vehicle_id=3&from=2026-09-01).
 */
export default {
  name: 'ResourcePage',
  components: { PageHeader, AppButton, AppIcon, AppPagination, AlertBanner, DataTable, EmptyState, ErrorState, LookupSelect, StatusBadge, ResourceForm },
  props: {
    config: { type: Object, required: true }
  },
  setup(props) {
    const route = useRoute()
    const query = { get: (key) => (typeof route.query[key] === 'string' ? route.query[key] : null) }
    const filters = {}
    for (const f of props.config.filters || []) {
      const initial = query.get(f.key)
      filters[f.key] = initial !== null ? initial : f.default ?? ''
    }
    const extra = {}
    if (props.config.dateRange) {
      extra.from = query.get('from') || ''
      extra.to = query.get('to') || ''
    }
    const listApi = useList((params, opts) => props.config.api.list(params, opts), {
      filters,
      extra,
      sort: props.config.defaultSort || '',
      search: props.config.search !== false
    })
    // Temps réel : un signal `resource.changed` concernant cette ressource recharge la liste
    // (`config.realtime` ajoute des ressources liées, ex. les affectations pour les véhicules).
    const off = subscribeRealtime({
      resources: [props.config.key, ...(props.config.realtime || [])].filter(Boolean),
      onChange: () => listApi.load()
    })
    onBeforeUnmount(off)
    return { list: listApi.state, load: listApi.load, toggleSort: listApi.toggleSort }
  },
  data() {
    return { formOpen: false, editing: null }
  },
  computed: {
    perms() {
      return this.config.permissions || {}
    },
    canCreate() {
      return !!this.config.fields && (!this.perms.create || session.can(this.perms.create))
    },
    tableColumns() {
      const cols = [...this.config.columns]
      if (this.hasRowActions) cols.push({ key: '_actions', label: '', align: 'right' })
      return cols
    },
    visibleFilters() {
      return (this.config.filters || []).filter((f) => !f.permission || session.can(f.permission))
    },
    newLabel() {
      return newLabel(this.config)
    },
    hasRowActions() {
      return this.config.rowActions !== false
    },
    hasActiveFilters() {
      const f = Object.values(this.list.filters).some((v) => v !== '' && !(Array.isArray(v) && !v.length))
      return f || !!this.list.search || !!this.list.extra.from || !!this.list.extra.to
    },
    rangeError() {
      const { from, to } = this.list.extra
      if (from && to && from > to) return 'La date de début doit précéder la date de fin.'
      if (from && to && daysBetween(from, to) + 1 > 366) return 'La période ne peut pas dépasser 366 jours.'
      return ''
    }
  },
  created() {
    this.load()
  },
  methods: {
    /** Texte d'une cellule : format() > type de colonne (money, date, datetime, bool) > valeur brute. */
    cellText(col, row, value) {
      if (col.format) return col.format(row)
      if (value === null || value === undefined || value === '') return '—'
      if (col.money) return session.money(value)
      if (col.date) return formatDate(value)
      if (col.datetime) return session.dateTime(value)
      if (col.bool) return value ? 'Oui' : 'Non'
      return value
    },
    filterValue(f) {
      return this.list.filters[f.key]
    },
    setFilter(f, value) {
      this.list.filters[f.key] = value
    },
    clearFilters() {
      for (const key of Object.keys(this.list.filters)) this.list.filters[key] = ''
      this.list.search = ''
      if (this.config.dateRange) {
        this.list.extra.from = ''
        this.list.extra.to = ''
      }
    },
    canEdit(row) {
      if (!this.config.fields) return false
      if (this.perms.update && !session.can(this.perms.update)) return false
      return this.config.editable ? this.config.editable(row) : true
    },
    canDelete(row) {
      if (this.perms.delete && !session.can(this.perms.delete)) return false
      if (!this.config.api.remove) return false
      return this.config.deletable ? this.config.deletable(row) : true
    },
    openCreate() {
      this.editing = null
      this.formOpen = true
    },
    openEdit(row) {
      this.editing = row
      this.formOpen = true
    },
    goDetail(row) {
      if (this.config.detailRoute) this.$router.push(this.config.detailRoute(row))
    },
    async remove(row) {
      const label = this.config.itemLabel ? this.config.itemLabel(row) : `#${row.id}`
      const ok = await confirmDialog({
        title: `Supprimer ${demonstrative(this.config)} ${this.config.singular} ?`,
        message: `« ${label} » sera supprimé${this.config.feminine ? 'e' : ''}. Cette action est définitive.`,
        confirmLabel: 'Supprimer',
        danger: true
      })
      if (!ok) return
      try {
        await this.config.api.remove(row.id)
        clearLookups()
        toast.success(`${this.config.singular.charAt(0).toUpperCase()}${this.config.singular.slice(1)} supprimé${this.config.feminine ? 'e' : ''}.`)
        await this.load()
      } catch (e) {
        // 409 (ressource liée), 403… : le message est déjà lisible, pas de succès présumé.
        toast.error(e.message)
      }
    }
  }
}
</script>
